import { Description, Visibility } from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import { Box, IconButton, Tooltip } from "@mui/material";
import { MaterialReactTable, MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";
import moment from "moment";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { getFormatoFechaExportacion } from "../../../../utils/datetimeUtils";
import { downloadCsv } from "../../../../utils/filesUtils";
import snackbarUtils from "../../../../utils/snackbarUtils";
import { dateAccesor } from "../../../commons/table/columns/accesors";
import { stringDateSort } from "../../../commons/table/columns/sorts";
import {
  localization,
  tableConfig,
} from "../../../commons/table/config/tableConfig";
import TitleContainer from "../../../commons/table/utilsTable/titleWithFetchData";
import { StyledAppointmentsTable } from "../../../styled/appointments";

const AppointmentsTable = ({
  data,
  namesArts,
  loading,
  noRefresh,
  fetchData,
  handleAppointmentDetail,
  handleOpenXmlPreview,
  estados,
}) => {
  const dispatch = useDispatch();


  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const columns = useMemo(() => [
    {
      header: "Art",
      accessorKey: "art",
      width: "10%",
      lookup: namesArts,
      Cell: ({ row }) => row.original.art.toUpperCase(),
      enableColumnFilter: !!namesArts,
    },
    {
      header: "Estado",
      accessorKey: "estado",
      filterSelectOptions: estados.map((estado) => estado.nombre),
      filterVariant: 'multi-select',
      Cell: ({ row }) => row.original.estado?.trim(),
    },
    {
      header: "Siniestro",
      accessorKey: "siniestro",
      width: "10%",
      Cell: ({ row }) => {
        if (row.original.siniestro === "0") return " ";
        return row.original.siniestro;
      },
    },
    { header: "Documento", accessorKey: "documento", width: "10%" },
    {
      header: "Empleador",
      accessorKey: "empleador",
      width: "20%",
    },
    {
      header: "Prestador",
      accessorKey: "prestador",
    },
    { header: "CUIT Prestador", accessorKey: "cuitPrestador" },
    {
      header: "Fecha Turno",
      accessorKey: "fechaTurno",
      width: "10%",
      accessorFn: dateAccesor("fechaTurno"),
      filterFn: "includesString",
      cellStyle: {
        textAlign: "center",
      },
      Cell: ({ row, column }) => {
        const fecha = row.original[column.id];
        if (fecha === null) {
          return "";
        }
        const diff = moment(fecha).diff(Date.now(), "days");

        return (
          <StyledAppointmentsTable.Chip
            variant="outlined"
            label={row.getValue(column.id)}
            error={diff < 0}
          />
        );
      },
      sortingFn: 'stringDate',
    },
    {
      header: "Fecha Procesado",
      accessorKey: "fechaProcesado",
      filtering: true,
      cellStyle: {
        textAlign: "center",
      },
      accessorFn: dateAccesor("fechaProcesado"),
      filterFn: "includesString",
      Cell: ({ row, column }) => {
        const fecha = row.original[column.id];
        if (fecha === null) {

          return "";
        }
        const diff = moment(fecha).diff(Date.now(), "days");

        return (
          <StyledAppointmentsTable.Chip
            variant="outlined"
            label={row.getValue(column.id)}
            error={diff < 0}
          />
        );
      },
      sortingFn: 'stringDate',
    },
    {
      header: "",
      id: "acciones",
      Cell: ({ row }) => (
        <>
          <Tooltip title="Ver Detalles">
            <IconButton
              onClick={() => handleAppointmentDetail(row.original)}
              size="small"
              color="inherit">
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Visualizar XML">
            <IconButton
              onClick={() => handleOpenXmlPreview(row.original)}
              size="small"
              color="inherit">
              <Description />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ], []);

  const formatTableDataForExport = (table) => {
    const tableDataWithFormat = table.map((fila) => [
      fila.art ? `"${fila.art}"` : "",
      fila.estado ? `"${fila.estado}"` : "",
      fila.siniestro ? `"${fila.siniestro}"` : "",
      fila.documento ? `"${fila.documento}"` : "",
      fila.empleador ? `"${fila.empleador}"` : "",
      fila.prestador ? `"${fila.prestador}"` : "",
      getFormatoFechaExportacion(fila.fechaTurno, "P"),
      getFormatoFechaExportacion(fila.fechaProcesado, "P"),
    ]);

    return tableDataWithFormat;
  };

  const handleExportToCsv = () => {
    setIsDownloadingCsv(true);

    const encabezado = [
      "Art",
      "Estado",
      "Siniestro",
      "Documento",
      "Empleador",
      "Prestador",
      "Fecha Turno",
      "Fecha Procesado",
    ];

    const formattedDataAsCsv = [encabezado, ...formatTableDataForExport(data)];

    let csvContent = "sep=,\n";
    csvContent += `${formattedDataAsCsv.map((e) => e.join(",")).join("\n")}`;

    downloadCsv(csvContent, "Turnos");
    snackbarUtils.success("Los turnos han sido descargados con éxito.");
    setIsDownloadingCsv(false);
  };

  return (
    <div>
      <MaterialReactTable
        {...tableConfig}
        columns={columns}
        data={data}
        state={{ isLoading: loading || isDownloadingCsv }}
        sortingFns={{
          stringDate: stringDateSort,
        }}
        renderTopToolbarCustomActions={() => (
          <Box m={0.5} display="flex" justifyContent="space-evenly">
            <TitleContainer
              title="Turnos"
              noRefresh={noRefresh}
              fetchData={fetchData}
            />

          </Box>
        )}

        defaultColumn={{
          maxSize: 100,
          minSize: 20,
          muiFilterTextFieldProps: {
            placeholder: '',
          },
        }}
        renderToolbarInternalActions={({ table }) => (
          <>
            <Tooltip title="Exportar CSV" arrow="top">
              <IconButton
                color="inherit"
                onClick={handleExportToCsv}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ToggleDensePaddingButton table={table} />
            <MRT_ToggleFullScreenButton table={table} />
          </>
        )}

        muiTableBodyCellProps={
          localization.cells
        }
      />
    </div>
  );
};

export default AppointmentsTable;